import { Checkbox } from '@progress/kendo-react-inputs'
import { useAtomValue } from 'jotai'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { HStack } from '~/components/elements/HStack'
import { IconButton } from '~/components/elements/IconButton'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { type StyledButtonProps } from '~/components/elements/StyledButton'
import { StyledInput } from '~/components/elements/StyledInput'
import { VStack } from '~/components/elements/VStack'
import { useFormField } from '~/hooks/useFormField'
import { ensureExists } from '~/utils'

import { dialogInfoAtom } from '../../atoms'
import { InputContainer } from '../InputContainer'

import { AttachedCustomerAttributeGroupSelector } from './AttachedCustomerAttributeGroupSelector'
import { LinkCustomerAttributesButton } from './LinkCustomerAttributesButton'
import { type InputValues } from './utils'

export type CustomerAttributeGroupInputProps = {
  index: number
  onDelete: (index: number) => void
}

export const CustomerAttributeGroupInput = ({
  index,
  onDelete,
}: CustomerAttributeGroupInputProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext<InputValues>()
  const nameField = useFormField(
    control,
    `customerAttributeGroups.${index}.name`,
  )
  const showCustomizedKPIField = useFormField(
    control,
    `customerAttributeGroups.${index}.showCustomizedKPI`,
  )

  const handleDeleteButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    onDelete(index)
  }, [index, onDelete])

  const dialogInfo = useAtomValue(dialogInfoAtom)
  const surveyGroup = ensureExists(
    dialogInfo?.surveyGroup,
    t('assertion_failed'),
  )

  return (
    <InputContainer>
      <VStack spacing={2}>
        <HStack spacing={0}>
          <IconButton
            buttonStyle="light"
            fillMode="flat"
            icon="delete"
            onClick={handleDeleteButtonClick}
          />

          <LabelingContainer label={t('customized_segments_group_name_2')}>
            <StyledInput {...nameField} />
          </LabelingContainer>
        </HStack>

        <LinkCustomerAttributesButton index={index} />

        <Checkbox
          label={t('show_as_customized_kpi')}
          {...showCustomizedKPIField}
        />
      </VStack>

      <VStack flex={1} spacing={4}>
        {surveyGroup.surveys.map((survey) => (
          <AttachedCustomerAttributeGroupSelector
            key={survey.id}
            index={index}
            survey={survey}
          />
        ))}
      </VStack>
    </InputContainer>
  )
}
